import { useState } from "react";
import { useAuthStore } from "../stores/auth";
import { ApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Field } from "../components/ui/input";
import { BrandMark } from "../components/ui/brand-mark";
import { blueprint } from "../blueprint";

export function Login() {
  const login = useAuthStore((s) => s.login);
  const verifyMfa = useAuthStore((s) => s.verifyMfa);
  const cancelMfa = useAuthStore((s) => s.cancelMfa);
  const mfaPending = useAuthStore((s) => s.mfaPending);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitPassword = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "too_many_attempts"
          ? "Trop de tentatives. Réessayez dans quelques minutes."
          : "Identifiants invalides",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfa(code);
    } catch (e) {
      if (e instanceof ApiError && (e.code === "mfa_expired" || e.code === "mfa_not_pending")) {
        cancelMfa();
        setCode("");
        setError("Délai dépassé : entrez de nouveau votre mot de passe.");
      } else {
        setError("Code refusé");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <form
        className="login__form"
        onSubmit={(e) => {
          e.preventDefault();
          void (mfaPending ? submitCode() : submitPassword());
        }}
      >
        <div className="login__head">
          <BrandMark size={40} />
          <div>
            <h1 className="login__title">{blueprint.name}</h1>
            {blueprint.tagline ? <p className="login__subtitle">{blueprint.tagline}</p> : null}
          </div>
        </div>

        {mfaPending ? (
          <>
            <p className="login__hint">
              Entrez le code à six chiffres de votre application d'authentification, ou un code de
              secours.
            </p>
            <Field label="Code de vérification">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Courriel">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </Field>
            <Field label="Mot de passe">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </Field>
          </>
        )}

        {error ? (
          <p className="login__error" role="alert">
            {error}
          </p>
        ) : null}
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? "Vérification…" : mfaPending ? "Valider le code" : "Se connecter"}
        </Button>
        {mfaPending ? (
          <Button variant="ghost" type="button" onClick={() => { cancelMfa(); setCode(""); setError(null); }}>
            Retour
          </Button>
        ) : null}
      </form>
    </div>
  );
}
